// This key is the home on the window object where we'll find an array of all the `ComponentRegistry` instances.
// Using this global array we can access our components from anywhere when no other avenue is available.

import {ComponentConfig} from "./component_config";

export const globalComponentRegistryCollectionKey = 'RankersComponentRegistryCollection'; // If you change this value then ensure that nothing else is accessing it like `window.RankersComponentRegistryCollection`.

/**
 * Adds a component registry to the global collection of component registries.
 * Once added, the component registry will be available for searching using the helper functions below.
 * @param componentRegistry
 */
export function addRegistryToGlobalCollection(componentRegistry) {
    // Set up a global collection of component registries if it doesn't already exist.
    const key = globalComponentRegistryCollectionKey;
    if (!window[key]) window[key] = {};
    if (!window[key].componentRegistries) window[key].componentRegistries = [];

    // Add helper functions to the globally available object so that they can be accessed from anywhere (ie. when not able to import from this file directly).
    if (!window[key].getGlobalComponentsOfType) window[key].getGlobalComponentsOfType = getGlobalComponentsOfType;
    if (!window[key].getGlobalComponentOfType) window[key].getGlobalComponentOfType = getGlobalComponentOfType;
    if (!window[key].getElementComponentsOfType) window[key].getElementComponentsOfType = getElementComponentsOfType;
    if (!window[key].getElementComponentOfType) window[key].getElementComponentOfType = getElementComponentOfType;

    // Add the component registry to the global collection.
    window[key].componentRegistries.push(componentRegistry);
}

/**
 * Given a type of component, this function will return an array of all the components of that type across all the component registries.
 * @param componentTypeKey {String} The key for the type of components that we're finding.
 * @returns {Component[]} An array of components of the componentTypeKey parameter.
 */
export function getGlobalComponentsOfType(componentTypeKey) {
    if (!(globalComponentRegistryCollectionKey in window)) {
      console.warn([`[getGlobalComponentsOfType] The global component registry collection key "${globalComponentRegistryCollectionKey}" does not exist on the window object.`]);
      return [];
    }
    if (!window[globalComponentRegistryCollectionKey].hasOwnProperty("componentRegistries")) {
      console.warn(`[getGlobalComponentsOfType] The global component registry collection key "${globalComponentRegistryCollectionKey}" does not have a "componentRegistries" property.`);
      return [];
    }
    let matchingComponents = [];
    window[globalComponentRegistryCollectionKey].componentRegistries.forEach(
      (componentRegistry) => {
        let components = componentRegistry.getComponentsForType(componentTypeKey);
        if (components && components.length > 0)
          matchingComponents.push(...components);
      },
    );
    return matchingComponents;
}

export function getGlobalComponentOfType(componentTypeKey) {
    return getGlobalComponentsOfType(componentTypeKey)[0];
}

/**
 * Given an element it finds the components (of a specific type) that are defined on it.
 * @param element {Element} The element to find components on.
 * @param componentTypeKey {String} The key for the type of components that we're finding on the element.
 * @returns {Component[]} An array of components of the componentTypeKey parameter.
 */
export function getElementComponentsOfType(element, componentTypeKey) {
    if (!element) return [];
    const components = element[ComponentConfig.elementComponentsKey];
    if (!components) return [];
    return components.filter(component => component.componentTypeKey === componentTypeKey);
}

export function getElementComponentOfType(element, componentTypeKey) {
    return getElementComponentsOfType(element, componentTypeKey)[0];
}